import React from 'react';
import { X, AlertTriangle, TrendingDown, Bitcoin, LineChart } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Modal detailing the 4 categories of broker fees that make actual PnL diverge
 * from what the app displays: (1) universal fees, (2) crypto perpetual funding,
 * (3) crypto spot commissions, (4) CFD overnight financing.
 */
export default function BrokerFeesModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="broker-fees-modal"
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors z-10"
          aria-label="Fermer"
          data-testid="broker-fees-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-11 h-11 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold mb-1">Frais broker : ce qui peut réduire ton PnL</h2>
              <p className="text-sm text-muted-foreground">
                Les calculs affichés dans TRADEX AI sont <strong>bruts</strong>. Voici ce que ton broker peut prélever en plus.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            {/* 1. Universal fees */}
            <section className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">1</span>
                Frais universels (tous brokers)
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
                <li><strong className="text-foreground">Spread bid/ask</strong> : payé à l'entrée, 0,01 % à 0,3 % selon l'actif</li>
                <li><strong className="text-foreground">Commission par ordre</strong> : 0 % à 0,25 % à l'entrée <em>ET</em> à la sortie</li>
                <li><strong className="text-foreground">Slippage</strong> : écart entre prix demandé et prix exécuté, jusqu'à 2 % sur news</li>
                <li><strong className="text-foreground">Conversion devise</strong> : 0,25 % à 1 % si compte € et actif $</li>
              </ul>
            </section>

            {/* 2. Crypto perpetual */}
            <section className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-amber-500/15 text-amber-400 text-xs font-bold flex items-center justify-center">2</span>
                <Bitcoin className="w-4 h-4 text-amber-400" />
                Crypto perpétuel (le plus impactant)
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Funding rate</strong> prélevé toutes les 8 h (00 h, 08 h, 16 h UTC). Formule :
                <span className="font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded mx-1">Notional × Funding Rate</span>.
                En marché bullish tendu, un LONG peut payer jusqu'à <strong className="text-amber-400">-0,3 % / 8 h</strong> = -0,9 % / jour.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
                <li>Binance USDT-M : maker 0,020 % / taker 0,050 % + funding</li>
                <li>Bybit : maker 0,020 % / taker 0,055 % + funding</li>
                <li>Bitget : maker 0,020 % / taker 0,060 % + funding</li>
                <li>MEXC : maker 0,000 % / taker 0,020 % + funding</li>
              </ul>
            </section>

            {/* 3. Crypto spot */}
            <section className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">3</span>
                <Bitcoin className="w-4 h-4 text-emerald-400" />
                Crypto spot (sans levier)
              </h3>
              <p className="text-sm text-muted-foreground mb-1">
                Pas de funding, mais commissions plus élevées qu'en perpétuel.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
                <li>Binance : 0,10 % maker/taker (0,075 % en payant en BNB)</li>
                <li>Coinbase Advanced : 0 – 0,40 % maker / 0,05 – 0,60 % taker</li>
                <li>Kraken : 0,25 % maker / 0,40 % taker</li>
              </ul>
            </section>

            {/* 4. CFD */}
            <section className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-rose-500/15 text-rose-400 text-xs font-bold flex items-center justify-center">4</span>
                <LineChart className="w-4 h-4 text-rose-400" />
                CFD (actions / indices / matières)
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Overnight financing</strong> calculé sur le notionnel (pas la marge) :
                <span className="font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded mx-1">Notional × (Taux ± Markup) / 365</span>.
                Charge <strong className="text-rose-400">triple le mercredi soir</strong> (compense le week-end).
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
                <li>IG : +1 % markup Forex, <strong>+3 %</strong> markup indices/actions/matières</li>
                <li>XTB : ~2,5 % markup annualisé</li>
                <li>Trading 212 : 3 – 5 % markup (haut du panier)</li>
                <li>eToro : ~5 – 6 % markup + admin fee après 7 jours</li>
              </ul>
              <div className="mt-3 p-2.5 rounded bg-rose-500/5 border border-rose-500/20 text-xs">
                <TrendingDown className="w-3.5 h-3.5 inline mr-1 text-rose-400" />
                <strong>Exemple concret :</strong> LONG SPX 10 000 $ × 10 chez IG pendant 30 jours = notionnel 100 000 $ → coût overnight ~<strong>410 $</strong>, invisible dans le PnL brut.
              </div>
            </section>
          </div>

          {/* Practical guidance */}
          <div className="mt-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-sm text-foreground/90">
              💡 <strong>Recommandation</strong> : renseigne dans le champ « Frais estimés » le total round-trip que ton broker te prélève typiquement.
              <br />
              <span className="text-muted-foreground text-xs">
                Ordres de grandeur : <strong>~0,15 %</strong> crypto perp scalp · <strong>~0,30 %</strong> crypto perp swing (2-3 jours) · <strong>~0,50 %</strong> CFD indices intraday · <strong>~1,5 %</strong> CFD 30 jours à levier
              </span>
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={onClose} data-testid="broker-fees-modal-ok">
              J'ai compris
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
